import { Injectable } from '@angular/core';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { ISubscription, IUserGroup } from '@core/interfaces/user-group.interface';
import { ComponentStore } from '@ngrx/component-store';
import { camelCase } from 'lodash';
import { catchError, EMPTY, MonoTypeOperatorFunction, Observable, pipe, switchMap, tap } from 'rxjs';
import { IUserGroupUpdatePayload } from '../../admin-user-group.interface';
import { AdminUserGroupService } from '../../admin-user-group.service';

interface IAdminUserGroupEditDialogState {
  subscriptions: ISubscription[] | null;
  updatedUserGroup: IUserGroup | null;
  saveRequested: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: IAdminUserGroupEditDialogState = {
  error: null,
  loading: false,
  saveRequested: false,
  subscriptions: null,
  updatedUserGroup: null,
};

@Injectable()
export class AdminUserGroupEditDialogStore extends ComponentStore<IAdminUserGroupEditDialogState> {
  public readonly updatedUserGroup$ = this.selectSignal(
    (state: IAdminUserGroupEditDialogState) => state.updatedUserGroup,
  );
  public readonly subscriptions$ = this.selectSignal((state: IAdminUserGroupEditDialogState) => state.subscriptions);
  public readonly loading$ = this.selectSignal((state: IAdminUserGroupEditDialogState) => state.loading);
  public readonly saveRequested$ = this.selectSignal((state: IAdminUserGroupEditDialogState) => state.saveRequested);
  public readonly error$ = this.selectSignal((state: IAdminUserGroupEditDialogState) => state.error);

  private readonly handleError = (errorMessage: string): MonoTypeOperatorFunction<unknown> =>
    pipe(
      catchError(() => {
        this.patchState({
          error: errorMessage,
          loading: false,
          saveRequested: false,
        });

        return EMPTY;
      }),
    );

  public readonly updateUserGroup = this.effect(
    (params$: Observable<{ id: string; payload: IUserGroupUpdatePayload }>) => {
      return params$.pipe(
        tap(() => this.patchState({ error: null, loading: true, saveRequested: true })),
        switchMap(({ id, payload }) =>
          this.adminUserGroupService.updateUserGroup(id, payload).pipe(
            tap((userGroup: IUserGroup) => {
              this.patchState({ loading: false, updatedUserGroup: userGroup });
            }),
            this.handleError('Failed to update user group'),
          ),
        ),
      );
    },
  );

  public readonly fetchSubscriptions = this.effect((trigger$: Observable<void>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ error: null, loading: true })),
      switchMap(() =>
        this.adminUserGroupService.getSubscriptions().pipe(
          tap((response: IApiResponse<ISubscription[]>) => {
            this.patchState({
              loading: false,
              subscriptions:
                response.data
                  ?.filter((subscription) => subscription.id !== 'admin')
                  .map((subscription) => ({
                    ...subscription,
                    labelKey: `shared.subscription.${camelCase(subscription.id)}`,
                  })) ?? [],
            });
          }),
          this.handleError('Failed to load subscriptions'),
        ),
      ),
    );
  });

  constructor(private readonly adminUserGroupService: AdminUserGroupService) {
    super(initialState);
  }
}
